import { TaskItem } from '@/components/tasks/task-line';
import { TaskItemV2 } from '@/components/tasks/task-line-v2.tsx';
import {
  ALL_FILTER,
  TasksFilter,
  type TasksPageFilters,
} from '@/components/tasks/tasks-filter.tsx';
import { AnimatedShinyText } from '@/components/ui/animated/animated-shiny-text';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MyLeadLine } from '@/components/ui/leads/my-lead-line';
import {
  PDKAiSparkle,
  PDKCheck,
  PDKCopilotIcon,
  PDKShowReasoning,
  PDKUndo,
} from '@/components/ui/pdk-icons';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getQueryParamsForScrollAndFocus } from '@/components/ux/hooks/use-scroll-and-focus';
import { useAuthContext } from '@/contexts/auth-context';
import {
  FEATURE_FLAG_NAMES,
  useIsFeatureEnabled,
} from '@/hooks/use-feature-flags';
import useGoToDeal from '@/hooks/use-go-to-deal';
import { pdkAxios } from '@/lib/pdk-axios';
import { getEntityTypeLabel, pluralize } from '@/lib/utils';
import { type AssignedTask, TaskV2 } from '@/models/registry';
import { assignedTaskStore, dealStore } from '@/models/stores';
import { leadStore } from '@/models/stores/leads';
import { taskStoreV2 } from '@/models/stores/tasks-v2.ts';
import { TaskableType, TaskStatus } from '@/models/types.ts';
import { myTasksCopilotStore } from '@/stores/my-tasks-copilot-store';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'framer-motion';
import { isEmpty } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';

export const Route = createFileRoute('/(authenticated)/my-tasks/')({
  component: MyTasksPageWrapper,
  staticData: {
    keepAlive: true,
  },
});

type MyTasksListProps = {
  tasks: AssignedTask[] | TaskV2[];
};

type TaskWithReason = {
  id: string;
  title: string;
  description: string;
  reason: string;
};

type GenerateTaskPriorityResponse = {
  prioritized_task_ids: string[];
  prioritized_tasks: TaskWithReason[];
};

const TASK_LINE_HEIGHT = 73;

const MyTasksList = observer(({ tasks }: MyTasksListProps) => {
  if (tasks.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        <div className="text-center">
          <p className="text-lg">No tasks assigned</p>
          <p className="text-sm mt-1">You're all caught up!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <AnimatePresence mode="popLayout">
        {tasks.map((task, index) => {
          const deal =
            task instanceof TaskV2
              ? dealStore.getById(task.taskable_id)
              : dealStore.getById(task.deal_id);

          return (
            <motion.div
              key={task.id}
              layout
              initial={false}
              transition={{
                layout: {
                  type: 'tween',
                  stiffness: 100,
                  damping: 30,
                  duration: 0.5,
                },
              }}
              animate={{
                transition: {
                  type: 'inertia',
                  stiffness: 100,
                  damping: 30,
                  duration: 1.5,
                },
                height: TASK_LINE_HEIGHT,
              }}
              style={{
                position: 'absolute',
                top: index * TASK_LINE_HEIGHT,
                left: 0,
                right: 0,
              }}
              className="border-b border-gray-200 dark:border-gray-800"
            >
              {task instanceof TaskV2 ? (
                <TaskItemV2 task={task} />
              ) : (
                <TaskItem task={task} deal={deal} />
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
});

const MyLeads = observer(() => {
  const leads = leadStore.all;

  const { user } = useAuthContext();

  const myLeads = useMemo(
    () => leads.filter((lead) => lead.original_sales_rep_id === user?.id),
    [leads, user?.id],
  );

  // Sort leads by priority (highest first)
  const sortedLeads = useMemo(() => {
    return [...myLeads].sort((a, b) => b.priority - a.priority);
  }, [myLeads]);

  if (sortedLeads.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        <div className="text-center">
          <p className="text-lg">No leads assigned</p>
          <p className="text-sm mt-1">Check back for new opportunities</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      {sortedLeads.map((lead) => (
        <MyLeadLine key={lead.customer_id} lead={lead} />
      ))}
    </div>
  );
});

const MyTasksPage = observer(() => {
  const { user } = useAuthContext();
  const { goToDeal } = useGoToDeal();
  const { isFeatureEnabled: isAiPrioritizeEnabled } = useIsFeatureEnabled({
    featureFlagName: FEATURE_FLAG_NAMES.AI_PRIORITIZE_TASKS,
  });
  const { isFeatureEnabled: isCopilotEnabled } = useIsFeatureEnabled({
    featureFlagName: FEATURE_FLAG_NAMES.MY_TASKS_COPILOT,
  });
  const [prioritizedTasks, setPrioritizedTasks] = useState<
    AssignedTask[] | null
  >(null);
  const [_prioritizationReasons, setPrioritizationReasons] = useState<
    Record<string, string>
  >({});
  // _prioritizationReasons can be used to show reasons for each task's priority
  const [showPrioritization, setShowPrioritization] = useState(false);
  const [showReasoning, setShowReasoning] = useState(false);

  const originalTasks = useMemo(() => {
    if (!user?.id) return [];

    const tasks = assignedTaskStore.allForActiveDeals.filter(
      (task) => task.assignee_id === user.id,
    );

    return tasks;
  }, [user?.id, assignedTaskStore.allForActiveDeals]);

  const myTasks = prioritizedTasks || originalTasks;

  const taskCounts = useMemo(() => {
    const incomplete = myTasks.filter(
      (task) => task.status === 'incomplete',
    ).length;
    const done = myTasks.filter((task) => task.status === 'done').length;
    const uniqueDealIds = new Set(myTasks.map((task) => task.deal_id));
    return {
      incomplete,
      done,
      total: myTasks.length,
      uniqueDeals: uniqueDealIds.size,
    };
  }, [myTasks]);

  const generatePriorityMutation = useMutation({
    mutationFn: async (tasks: AssignedTask[]) => {
      const payload = {
        tasks: tasks.map((task) => ({
          id: task.id,
          title: task.task?.title || '',
          description: task.task?.description || '',
        })),
      };
      const response = await pdkAxios.post<GenerateTaskPriorityResponse>(
        '/ml/generate-task-priority',
        payload,
        {
          timeout: 10 * 60 * 1000, // 10 minutes
        },
      );
      return response.data;
    },
    onSuccess: (data) => {
      const taskMap = new Map(incompleteTasks.map((t) => [t.id, t]));
      const prioritizedIncompleteTasks = data.prioritized_task_ids
        .map((id: string) => taskMap.get(id))
        .filter(
          (t: AssignedTask | undefined): t is AssignedTask => t !== undefined,
        );

      // Get done tasks to append after prioritized incomplete tasks
      const doneTasks = originalTasks.filter((task) => task.status === 'done');
      const reorderedTasks = [...prioritizedIncompleteTasks, ...doneTasks];

      const reasons: Record<string, string> = {};
      data.prioritized_tasks.forEach((task: TaskWithReason) => {
        reasons[task.id] = task.reason;
      });

      setPrioritizedTasks(reorderedTasks);
      setPrioritizationReasons(reasons);
      setShowPrioritization(true);
    },
  });

  const incompleteTasks = useMemo(() => {
    return originalTasks.filter((task) => task.status === 'incomplete');
  }, [originalTasks]);

  const handleGeneratePriority = () => {
    generatePriorityMutation.mutate(incompleteTasks);
  };

  const handleAcceptPrioritization = () => {
    setShowPrioritization(false);
  };

  const handleRevertPrioritization = () => {
    setPrioritizedTasks(null);
    setShowPrioritization(false);
  };

  const handleShowReasoning = () => {
    setShowReasoning(true);
  };

  const handleCloseReasoning = () => {
    setShowReasoning(false);
  };

  const handleStartCopilot = () => {
    if (myTasks.length === 0) return;

    myTasksCopilotStore.startCopilot(myTasks, _prioritizationReasons);

    const firstTask = myTasks[0];
    const deal = dealStore.getById(firstTask.deal_id);

    const queryParams = getQueryParamsForScrollAndFocus({
      uiType: 'task',
      id: firstTask.id,
    });

    if (deal?.short_id) {
      goToDeal({
        dealShortId: deal.short_id,
        queryParams,
      });
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-none p-6 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl  text-gray-900 dark:text-white">
              My Tasks
            </h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {taskCounts.incomplete > 0 && (
                <>
                  {taskCounts.incomplete} incomplete{' '}
                  {taskCounts.done > 0 && `• ${taskCounts.done} done • `}
                </>
              )}
              {taskCounts.incomplete === 0 && taskCounts.done > 0 && (
                <>
                  {taskCounts.done} completed{' '}
                  {pluralize(taskCounts.done, 'task', 'tasks')} •{' '}
                </>
              )}
              {taskCounts.total > 0 ? (
                <>
                  across {taskCounts.uniqueDeals}{' '}
                  {pluralize(taskCounts.uniqueDeals, 'deal', 'deals')}
                </>
              ) : (
                'No tasks assigned'
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isCopilotEnabled && !showPrioritization && (
              <Button
                variant="outline"
                onClick={handleStartCopilot}
                disabled={myTasks.length === 0}
                className="flex items-center gap-2"
              >
                <PDKCopilotIcon className="h-4 w-4" />
                My Tasks Copilot
              </Button>
            )}
            {isAiPrioritizeEnabled && !showPrioritization && (
              <>
                <Button
                  variant="outline"
                  onClick={handleGeneratePriority}
                  disabled={
                    generatePriorityMutation.isPending ||
                    incompleteTasks.length === 0
                  }
                  className="flex items-center gap-2"
                >
                  <PDKAiSparkle className="h-4 w-4" />

                  {generatePriorityMutation.isPending ? (
                    <AnimatedShinyText>Generating...</AnimatedShinyText>
                  ) : (
                    'AI Prioritize'
                  )}
                </Button>
              </>
            )}
          </div>
        </div>

        {showPrioritization && (
          <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <PDKAiSparkle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <p className="text-blue-900 dark:text-blue-100">
                  AI-Suggested Priority Order
                </p>
              </div>
            </div>
            <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
              Tasks have been reordered based on what would make your day most
              productive.
            </p>
            <div className="flex gap-2">
              <Button
                variant="default"
                size="sm"
                onClick={handleAcceptPrioritization}
                className="flex items-center gap-2"
              >
                <PDKCheck className="h-4 w-4" />
                Keep This Order
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRevertPrioritization}
                className="flex items-center gap-2"
              >
                <PDKUndo className="h-4 w-4" />
                Revert to Original
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleShowReasoning}
                className="flex items-center gap-2"
              >
                <PDKShowReasoning className="h-4 w-4" />
                Show Reasoning
              </Button>
            </div>
          </div>
        )}
      </div>

      <Tabs defaultValue="tasks" className="flex-1 flex flex-col">
        <TabsList className="mx-6 mb-0 mt-4">
          <TabsTrigger value="tasks">My Tasks</TabsTrigger>
          <TabsTrigger value="leads">My Leads</TabsTrigger>
        </TabsList>
        <TabsContent value="tasks" className="flex-1 min-h-0 relative mt-0">
          <MyTasksList tasks={myTasks} />
        </TabsContent>
        <TabsContent value="leads" className="flex-1 min-h-0 relative mt-0">
          <MyLeads />
        </TabsContent>
      </Tabs>

      <Dialog open={showReasoning} onOpenChange={setShowReasoning}>
        <DialogContent
          className="max-w-2xl max-h-[80vh] overflow-y-auto"
          onClickClose={handleCloseReasoning}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PDKAiSparkle className="h-5 w-5" />
              AI Task Prioritization Reasoning
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {Object.entries(_prioritizationReasons).map(([taskId, reason]) => {
              const task = originalTasks.find((t) => t.id === taskId);
              if (!task) return null;

              return (
                <div
                  key={taskId}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                >
                  <h3 className="text-gray-900 dark:text-gray-100 mb-2">
                    {task.task?.title || 'Untitled Task'}
                  </h3>
                  {task.task?.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      {task.task.description}
                    </p>
                  )}
                  <div className="bg-blue-50 dark:bg-blue-950/30 p-3 rounded border-l-4 border-blue-200 dark:border-blue-800">
                    <p className="text-sm text-blue-900 dark:text-blue-100">
                      <span>AI Reasoning:</span> {reason}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
});

function MyTasksPageWrapper() {
  const { isFeatureEnabled: isGeneralTasksEnabled } = useIsFeatureEnabled({
    featureFlagName: FEATURE_FLAG_NAMES.GENERALIZED_TASKS,
  });
  return isGeneralTasksEnabled ? <MyTasksPageV2 /> : <MyTasksPage />;
}

const MyTasksPageV2 = observer(() => {
  const { user } = useAuthContext();
  const { goToDeal } = useGoToDeal();
  const { isFeatureEnabled: isAiPrioritizeEnabled } = useIsFeatureEnabled({
    featureFlagName: FEATURE_FLAG_NAMES.AI_PRIORITIZE_TASKS,
  });
  const { isFeatureEnabled: isCopilotEnabled } = useIsFeatureEnabled({
    featureFlagName: FEATURE_FLAG_NAMES.MY_TASKS_COPILOT,
  });
  const [prioritizedTasks, setPrioritizedTasks] = useState<TaskV2[] | null>(
    null,
  );
  const [_prioritizationReasons, setPrioritizationReasons] = useState<
    Record<string, string>
  >({});
  // _prioritizationReasons can be used to show reasons for each task's priority
  const [showPrioritization, setShowPrioritization] = useState(false);
  const [showReasoning, setShowReasoning] = useState(false);
  const [filters, setFilters] = useState<TasksPageFilters>({
    entityTypes: [ALL_FILTER],
    statuses: [ALL_FILTER],
  });

  const originalTasks = useMemo(() => {
    if (!user?.id) return [];

    const tasks = taskStoreV2.allTasksForActiveEntities.filter(
      (task) => task.assignee_id === user.id,
    );

    return tasks;
  }, [user?.id, taskStoreV2.allTasksForActiveEntities]);

  const filteredTasks = useMemo(() => {
    let tasks = originalTasks;

    if (
      !isEmpty(filters.entityTypes) &&
      !filters.entityTypes?.includes(ALL_FILTER)
    ) {
      tasks = tasks.filter((task) =>
        filters.entityTypes!.includes(task.taskable_type),
      );
    }

    if (!isEmpty(filters.statuses) && !filters.statuses?.includes(ALL_FILTER)) {
      tasks = tasks.filter((task) => filters.statuses!.includes(task.status));
    }

    const query = filters.searchQuery;
    if (query) {
      tasks = tasks.filter((t) =>
        t.title.toLowerCase().includes(query.toLowerCase()),
      );
    }

    return tasks;
  }, [filters, originalTasks]);

  const myTasks = prioritizedTasks || filteredTasks;

  const taskCounts = useMemo(() => {
    const incomplete = myTasks.filter(
      (task) => task.status === TaskStatus.Incomplete,
    ).length;
    const done = myTasks.filter(
      (task) => task.status === TaskStatus.Done,
    ).length;

    const entityCounts: Record<string, Set<string>> = {};
    myTasks.forEach((task) => {
      if (!entityCounts[task.taskable_type]) {
        entityCounts[task.taskable_type] = new Set();
      }
      entityCounts[task.taskable_type].add(task.taskable_id);
    });

    const totalEntities = Object.values(entityCounts).reduce(
      (sum, set) => sum + set.size,
      0,
    );

    return {
      incomplete,
      done,
      total: myTasks.length,
      uniqueEntities: totalEntities,
      byType: entityCounts,
    };
  }, [myTasks]);

  const generatePriorityMutation = useMutation({
    mutationFn: async (tasks: TaskV2[]) => {
      const payload = {
        tasks: tasks.map((task) => ({
          id: task.id,
          title: task.task?.title || '',
          description: task.task?.description || '',
        })),
      };
      const response = await pdkAxios.post<GenerateTaskPriorityResponse>(
        '/ml/generate-task-priority',
        payload,
        {
          timeout: 10 * 60 * 1000, // 10 minutes
        },
      );
      return response.data;
    },
    onSuccess: (data) => {
      const taskMap = new Map(incompleteTasks.map((t) => [t.id, t]));
      const prioritizedIncompleteTasks = data.prioritized_task_ids
        .map((id: string) => taskMap.get(id))
        .filter((t: TaskV2 | undefined): t is TaskV2 => t !== undefined);

      // Get done tasks to append after prioritized incomplete tasks
      const doneTasks = originalTasks.filter(
        (task) => task.status === TaskStatus.Done,
      );
      const reorderedTasks = [...prioritizedIncompleteTasks, ...doneTasks];

      const reasons: Record<string, string> = {};
      data.prioritized_tasks.forEach((task: TaskWithReason) => {
        reasons[task.id] = task.reason;
      });

      setPrioritizedTasks(reorderedTasks);
      setPrioritizationReasons(reasons);
      setShowPrioritization(true);
    },
  });

  const incompleteTasks = useMemo(() => {
    return originalTasks.filter(
      (task) => task.status === TaskStatus.Incomplete,
    );
  }, [originalTasks]);

  const handleGeneratePriority = () => {
    generatePriorityMutation.mutate(incompleteTasks);
  };

  const handleAcceptPrioritization = () => {
    setShowPrioritization(false);
  };

  const handleRevertPrioritization = () => {
    setPrioritizedTasks(null);
    setShowPrioritization(false);
  };

  const handleShowReasoning = () => {
    setShowReasoning(true);
  };

  const handleCloseReasoning = () => {
    setShowReasoning(false);
  };

  const handleStartCopilot = () => {
    if (myTasks.length === 0) return;

    myTasksCopilotStore.startCopilot(myTasks, _prioritizationReasons);

    const firstTask = myTasks[0];

    if (firstTask.taskable_type === TaskableType.Deal) {
      const deal = dealStore.getById(firstTask.taskable_id);
      const queryParams = getQueryParamsForScrollAndFocus({
        uiType: 'task',
        id: firstTask.id,
      });

      if (deal?.short_id) {
        goToDeal({
          dealShortId: deal.short_id,
          queryParams,
        });
      }
    } else if (firstTask.taskable_type === TaskableType.Appraisal) {
      // TODO -- appraisal navigation
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-none p-6 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl  text-gray-900 dark:text-white">
              My Tasks
            </h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {taskCounts.incomplete > 0 && (
                <>
                  {taskCounts.incomplete} incomplete{' '}
                  {taskCounts.done > 0 && `• ${taskCounts.done} done • `}
                </>
              )}
              {taskCounts.incomplete === 0 && taskCounts.done > 0 && (
                <>
                  {taskCounts.done} completed{' '}
                  {pluralize(taskCounts.done, 'task', 'tasks')} •{' '}
                </>
              )}
              {taskCounts.total > 0 ? (
                <>
                  across{' '}
                  {Object.keys(taskCounts.byType).length === 1 ? (
                    <>
                      {taskCounts.uniqueEntities}{' '}
                      {getEntityTypeLabel(
                        Object.keys(taskCounts.byType)[0],
                        taskCounts.uniqueEntities,
                      )}
                    </>
                  ) : (
                    <>
                      {Object.entries(taskCounts.byType).map(
                        ([type, ids], index) => {
                          const count = ids.size;
                          const isLast =
                            index ===
                            Object.entries(taskCounts.byType).length - 1;
                          return (
                            <span key={type}>
                              {count} {getEntityTypeLabel(type, count)}
                              {!isLast && ', '}
                            </span>
                          );
                        },
                      )}
                    </>
                  )}
                </>
              ) : (
                'No tasks assigned'
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isCopilotEnabled && !showPrioritization && (
              <Button
                variant="outline"
                onClick={handleStartCopilot}
                disabled={myTasks.length === 0}
                className="flex items-center gap-2"
              >
                <PDKCopilotIcon className="h-4 w-4" />
                My Tasks Copilot
              </Button>
            )}
            {isAiPrioritizeEnabled && !showPrioritization && (
              <>
                <Button
                  variant="outline"
                  onClick={handleGeneratePriority}
                  disabled={
                    generatePriorityMutation.isPending ||
                    incompleteTasks.length === 0
                  }
                  className="flex items-center gap-2"
                >
                  <PDKAiSparkle className="h-4 w-4" />

                  {generatePriorityMutation.isPending ? (
                    <AnimatedShinyText>Generating...</AnimatedShinyText>
                  ) : (
                    'AI Prioritize'
                  )}
                </Button>
              </>
            )}
          </div>
        </div>

        {showPrioritization && (
          <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <PDKAiSparkle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <p className="text-blue-900 dark:text-blue-100">
                  AI-Suggested Priority Order
                </p>
              </div>
            </div>
            <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
              Tasks have been reordered based on what would make your day most
              productive.
            </p>
            <div className="flex gap-2">
              <Button
                variant="default"
                size="sm"
                onClick={handleAcceptPrioritization}
                className="flex items-center gap-2"
              >
                <PDKCheck className="h-4 w-4" />
                Keep This Order
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRevertPrioritization}
                className="flex items-center gap-2"
              >
                <PDKUndo className="h-4 w-4" />
                Revert to Original
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleShowReasoning}
                className="flex items-center gap-2"
              >
                <PDKShowReasoning className="h-4 w-4" />
                Show Reasoning
              </Button>
            </div>
          </div>
        )}
      </div>

      <Tabs defaultValue="tasks" className="flex-1 flex flex-col">
        <TabsList className="mx-6 mb-0 mt-4">
          <TabsTrigger value="tasks">My Tasks</TabsTrigger>
          <TabsTrigger value="leads">My Leads</TabsTrigger>
        </TabsList>
        <div className="px-6 py-3 border-b">
          <TasksFilter
            filters={filters}
            onChange={setFilters}
            hideAssignee={true}
          />
        </div>
        <TabsContent value="tasks" className="flex-1 min-h-0 relative mt-0">
          <MyTasksList tasks={myTasks} />
        </TabsContent>
        <TabsContent value="leads" className="flex-1 min-h-0 relative mt-0">
          <MyLeads />
        </TabsContent>
      </Tabs>

      <Dialog open={showReasoning} onOpenChange={setShowReasoning}>
        <DialogContent
          className="max-w-2xl max-h-[80vh] overflow-y-auto"
          onClickClose={handleCloseReasoning}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PDKAiSparkle className="h-5 w-5" />
              AI Task Prioritization Reasoning
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {Object.entries(_prioritizationReasons).map(([taskId, reason]) => {
              const task = originalTasks.find((t) => t.id === taskId);
              if (!task) return null;

              return (
                <div
                  key={taskId}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                >
                  <h3 className="text-gray-900 dark:text-gray-100 mb-2">
                    {task.task?.title || 'Untitled Task'}
                  </h3>
                  {task.task?.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      {task.task.description}
                    </p>
                  )}
                  <div className="bg-blue-50 dark:bg-blue-950/30 p-3 rounded border-l-4 border-blue-200 dark:border-blue-800">
                    <p className="text-sm text-blue-900 dark:text-blue-100">
                      <span>AI Reasoning:</span> {reason}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
});
